/**
 * 1.6.0 Administration → Access → AI assistants (admin; plan A3). Stream T's file (AccessAdmin.tsx only mounts it):
 *
 *  - "Allow AI assistant access" (off by default), the roles that may create tokens (administrators only by default),
 *    the maximum expiry, "Read-only only" (useAssistantSettings, useSaveAssistantSettings);
 *  - every person's tokens with Revoke (useAdminTokens, useAdminRevokeToken).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Loader2, ShieldOff } from 'lucide-react';
import type { AdminUserTokenDTO, AssistantSettingsInput } from '@prism/contracts';
import { errorMessage } from '../../../api/client';
import { useAdminRevokeToken, useAdminTokens, useAssistantSettings, useRoles, useSaveAssistantSettings } from '../../../api/hooks';
import { StatusDot } from '../../../components/Status';
import { toast } from '../../../components/Toasts';
import { relative } from '../../../lib/format';

const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export function AssistantsAdmin() {
  const q = useAssistantSettings();
  const save = useSaveAssistantSettings();
  const roles = useRoles().data?.items ?? [];
  const [p, setP] = useState<AssistantSettingsInput | null>(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (q.data && !p) setP({ enabled: q.data.enabled, tokenRoles: q.data.tokenRoles, maxExpiryDays: q.data.maxExpiryDays, readOnlyOnly: q.data.readOnlyOnly });
  }, [q.data, p]);
  if (!p || !q.data) return <section className="panel-card" style={{ marginBottom: 20 }} id="ai-assistants"><div className="skeleton" style={{ height: 120 }} /></section>;
  const saved = { enabled: q.data.enabled, tokenRoles: q.data.tokenRoles, maxExpiryDays: q.data.maxExpiryDays, readOnlyOnly: q.data.readOnlyOnly };
  const dirty = JSON.stringify({ ...p, tokenRoles: [...p.tokenRoles].sort() }) !== JSON.stringify({ ...saved, tokenRoles: [...saved.tokenRoles].sort() });

  async function submit() {
    if (!p) return;
    if (!p.enabled && q.data?.enabled && q.data.liveTokens > 0 && !window.confirm(`Turn assistant access off? The ${q.data.liveTokens} token${q.data.liveTokens === 1 ? '' : 's'} in use stop working at once (they work again if it is turned back on).`)) return;
    try {
      const r = await save.mutateAsync(p);
      setP({ enabled: r.enabled, tokenRoles: r.tokenRoles, maxExpiryDays: r.maxExpiryDays, readOnlyOnly: r.readOnlyOnly });
      toast('AI assistant settings saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <section className="panel-card" style={{ marginBottom: 20 }} id="ai-assistants" data-testid="assistants-admin">
      <h3><Bot size={16} color="var(--accent-strong)" /> AI assistants</h3>
      <p className="muted" style={{ margin: 0 }}>People connect an AI assistant (Claude Desktop, Claude Code or another MCP client) to PRISM with a personal token and the
        {' '}<Link to="/admin/integrations">PRISM MCP connector</Link>. A token acts as the person who made it: it sees and changes only what they may, and it never reaches
        {' '}their account, Access, API keys or other tokens. Changes it makes are recorded as theirs, “via assistant”.</p>
      <label className="toggle-row"><input type="checkbox" checked={p.enabled} onChange={(e) => setP({ ...p, enabled: e.target.checked })} data-testid="assistants-enabled" />
        <span><b>Allow AI assistant access</b><br /><span className="faint" style={{ fontSize: 12 }}>Off (the default): no tokens can be made, and every existing token is refused.</span></span></label>
      <div className="field-row">
        <div className="field"><label>Roles that may create tokens</label>
          <div className="check-list" data-testid="assistants-roles">{roles.map((r) => (
            <label key={r.key}><input type="checkbox" checked={p.tokenRoles.includes(r.key)} onChange={() => setP({ ...p, tokenRoles: toggle(p.tokenRoles, r.key) })} data-testid={`assistants-role-${r.key}`} /> {r.name}</label>
          ))}</div>
          <span className="faint" style={{ fontSize: 12 }}>Administrators only by default. Taking a role away keeps its members’ tokens working; revoke them below.</span>
        </div>
        <div className="field">
          <label htmlFor="assistants-max-expiry">Longest expiry</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input id="assistants-max-expiry" className="input" type="number" min={1} max={365} step={1} style={{ width: 90 }} value={p.maxExpiryDays}
              onChange={(e) => setP({ ...p, maxExpiryDays: Math.max(1, Math.min(365, Math.floor(Number(e.target.value) || 1))) })} data-testid="assistants-max-expiry" />
            <span className="muted">days</span>
          </div>
          <span className="faint" style={{ fontSize: 12 }}>People choose 30, 90 or 365 days, up to this.</span>
          <label className="toggle-row" style={{ marginTop: 8 }}><input type="checkbox" checked={p.readOnlyOnly} onChange={(e) => setP({ ...p, readOnlyOnly: e.target.checked })} data-testid="assistants-read-only-only" />
            <span><b>Read-only only</b><br /><span className="faint" style={{ fontSize: 12 }}>Only read-only tokens can be made, and existing tokens act read-only.</span></span></label>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary sm" disabled={!dirty || save.isPending} onClick={() => void submit()} data-testid="assistants-save">{save.isPending && <Loader2 size={13} className="spin" />} Save</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <p className="section-label" style={{ margin: 0, flex: 1 }}>Tokens · {q.data.liveTokens} in use{q.data.lastUsedAt ? ` · last used ${relative(q.data.lastUsedAt)}` : ''}</p>
        <label className="faint" style={{ fontSize: 12 }}><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="assistants-show-all" /> Show revoked and expired</label>
      </div>
      <AdminTokenTable state={showAll ? 'all' : 'live'} />
    </section>
  );
}

function AdminTokenTable({ state }: { state: 'live' | 'all' }) {
  const q = useAdminTokens(state);
  const revoke = useAdminRevokeToken();
  const items = q.data?.items ?? [];
  async function onRevoke(t: AdminUserTokenDTO) {
    if (!window.confirm(`Revoke “${t.name}” of ${t.user.displayName}? Their assistant stops working with it at once.`)) return;
    try {
      await revoke.mutateAsync(t.id);
      toast(`Revoked ${t.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  if (q.isLoading) return <div className="skeleton" style={{ height: 60 }} />;
  if (!items.length) return <p className="faint" style={{ margin: 0 }} data-testid="assistants-no-tokens">{state === 'live' ? 'No tokens in use.' : 'No tokens yet.'}</p>;
  return (
    <div className="table-scroll">
      <table className="table" aria-label="Assistant tokens" data-testid="assistants-tokens">
        <thead><tr><th>Person</th><th>Token</th><th>Access</th><th>Last used</th><th>Expires</th><th>Status</th><th aria-label="Actions" /></tr></thead>
        <tbody>{items.map((t) => (
          <tr key={t.id} data-testid="assistants-token-row" style={t.state !== 'live' ? { opacity: 0.6 } : undefined}>
            <td><strong>{t.user.displayName}</strong><div className="faint" style={{ fontSize: 12 }}>{t.user.email}{t.user.disabled ? ' · disabled' : ''}</div></td>
            <td>{t.name}<div className="mono faint" style={{ fontSize: 12 }}>{t.prefix}…</div></td>
            <td className="muted">{t.readOnly ? 'Read-only' : 'Read and change'}{t.writeLog ? ' · log descriptions' : ''}</td>
            <td className="muted">{t.lastUsedAt ? `${relative(t.lastUsedAt)}${t.lastUsedIp ? ` · ${t.lastUsedIp}` : ''}` : 'Never'}</td>
            <td className="muted">{relative(t.expiresAt)}</td>
            <td><StatusDot tone={t.state === 'live' ? 'ready' : 'failed'}>{t.state}</StatusDot></td>
            <td style={{ textAlign: 'right' }}>{t.state === 'live' && <button className="btn ghost sm danger" onClick={() => void onRevoke(t)} data-testid="assistants-revoke"><ShieldOff size={13} /> Revoke</button>}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
